import { useState, useEffect } from 'react';
import { printingService } from '../services/printingService';
import { settingsService } from '../services/settingsService';
import type { Report } from '../types/printing';
import { useAuth } from '../contexts/AuthContext';

export interface UseActiveReportsReturn {
  /** Aktive rapporter av gitt ReportType (tenant + delte master-maler, merged). */
  reports: Report[];
  /** Valgt rapport-ID (default = «DefaultReportId:{type}», ellers første aktive). undefined hvis ingen finnes. */
  selectedReportId: number | undefined;
  setSelectedReportId: (reportId: number | undefined) => void;
  loading: boolean;
}

/**
 * Laster tenantens aktive rapporter av en gitt type og holder styr på valgt
 * rapport — generalisering av useSsccReports (SortingBox-mønsteret): kalleren
 * sender `reportId` eksplisitt i stedet for at backend defaulter til legacy-ID.
 *
 * Forhåndsvelger malen i «DefaultReportId:{type}» (profil → system) når den er
 * blant de aktive; står den på 0/Auto eller peker på en inaktiv mal, velges
 * første aktive. Ved flere viser kalleren en dropdown.
 * Ved 0 rapporter forblir selectedReportId undefined — backend bruker da
 * DefaultReportId-innstillingen eller sin siste-utvei-default.
 */
export function useActiveReports(reportType: number): UseActiveReportsReturn {
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user?.companyNo) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      printingService.getReports(user.companyNo, reportType, true),
      // Standardmal fra «DefaultReportId:{type}» (profil → system). Feil her skal
      // ikke hindre malvalget — da brukes første aktive som før.
      settingsService.getEffectiveSetting(`DefaultReportId:${reportType}`).catch(() => null)
    ])
      .then(([list, defaultSetting]) => {
        if (cancelled) return;
        setReports(list);
        const defaultId = Number(defaultSetting?.currentValue);
        const preferred = defaultId > 0 ? list.find((r) => r.reportID === defaultId) : undefined;
        setSelectedReportId((prev) => prev ?? preferred?.reportID ?? (list.length > 0 ? list[0].reportID : undefined));
      })
      .catch((err) => {
        console.warn(`Kunne ikke laste rapporter av type ${reportType}:`, err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.companyNo, reportType]);

  return { reports, selectedReportId, setSelectedReportId, loading };
}
